import axios from 'axios'
import Settings from 'app/config/appConfig'
import { navigator } from 'app/navigation/navigator'
import { showMessage } from '@/modules/shared/stores/messageStore'
import { useLegacyServiceLoadingStore } from '@/modules/shared/stores/legacyServiceLoadingStore'
import { showGenericError } from '@/modules/shared/stores/genericErrorStore'
import { MessageType } from 'app/constants/messageType'
import { ROUTES } from 'app/constants/routes'
import contentDisposition from 'content-disposition'
import {
  dispatchCompanyErrorModal,
  dispatchIpNotAllowedModal,
  getValidAccessToken,
} from '@/lib/axios'
import { redirectToLogin, userManager } from '@/config'

// Spinner tej warstwy liczy sie teraz w `legacyServiceLoadingStore` (zustand), nie w
// reduxowym slice'ie `service`. Dzieki temu `baseService` nie potrzebuje juz
// `dispatch`, ktory przewlekal sie przez sygnatury 26 plikow serwisowych i kazde
// wywolanie API.
//
// To NIE jest wspoldzielony `loadingStore`: tamten czyta 58 plikow nowego UI, a ten
// licznik ma dokladnie trzech czytelnikow, tak jak stary slice. Szczegoly w store'ie.
//
// `serviceCallError` zniknelo bez zamiennika, bo nic go nie czytalo: ustawialo
// `state.service.error`/`.details`, ktorych zaden komponent nie odczytywal, a licznik
// zostawialo nietkniety (wszystkie wywolania tutaj szly z handled=true). Byla to
// martwa akcja, nie kanal komunikatu.
const startLoading = () =>
  useLegacyServiceLoadingStore.getState().startLoading()

const timeout: number = 300000
const errorTimeout: number = 300000

const STATUS_CODES_FOR_INTERNAL_HANDLING = [401, 500, 504]
export const serviceURL: string = `${Settings.serverAddress}`

export const downloadFile = async (
  url: string,
  data: any,
  handleErrorInternally: boolean = true,
  mimeType: string = 'application/pdf',
): Promise<any> => {
  const token = await getValidAccessToken()
  return new Promise((resolve, reject) => {
    const CancelToken = axios.CancelToken
    const source = CancelToken.source()

    const stopLoading = startLoading()

    const instance = axios.create({
      baseURL: serviceURL,
      timeout: 5 * 60 * 1000,
      cancelToken: source.token,
      responseType: 'arraybuffer',
    })

    instance.interceptors.request.use((config) => {
      if (token) {
        config.headers = {
          ...config.headers,
          Authorization: `Bearer ${token}`,
        }
      }
      return config
    })
    instance
      .post(url, data)
      .then((response) => {
        const headerCt =
          response.headers['content-type'] || response.headers['Content-Type']
        const effectiveType = headerCt
          ? String(headerCt).split(';')[0].trim()
          : mimeType
        const file = new Blob([response.data], { type: effectiveType })
        const filename = resolveDownloadFileName(response)
        const fileURL = URL.createObjectURL(file)
        openFile(fileURL, filename)

        stopLoading()
        resolve({ status: response.status, content: response })
      })
      .catch((error) => {
        stopLoading()
        if (error.response && error.response.status == 400) {
          // The response is an arraybuffer (responseType above). Buffer is a Node global
          // and does not exist in the browser, so this threw ReferenceError and swallowed
          // every 400 before its code could be shown. Same decode as the newer
          // modules/printouts/services/BaseService.tsx.
          let msg = 'EMPTY_DOCUMENT'
          try {
            const raw = error.response.data
            const text =
              typeof raw === 'string'
                ? raw
                : new TextDecoder('utf-8').decode(new Uint8Array(raw))
            const errorCode = JSON.parse(text)
            if (errorCode?.code) {
              msg = errorCode.code
            }
          } catch {
            /* keep the default code */
          }
          handleServiceError(
            error,
            handleErrorInternally,
            reject,
            'errorCodes.document-generation-failed',
            msg,
          )
        } else {
          handleServiceError(error, handleErrorInternally, reject)
        }
      })
  })
}
export const get = async (
  url: string,
  handleErrorInternally: boolean = true,
  avoidServiceCallsNotification: boolean = false,
): Promise<any> => {
  const token = await getValidAccessToken()
  return new Promise((resolve, reject) => {
    const CancelToken = axios.CancelToken
    const source = CancelToken.source()

    let isFulfilled = false
    const stopLoading = avoidServiceCallsNotification ? null : startLoading()

    const instance = axios.create({
      baseURL: serviceURL,
      timeout: timeout,
      cancelToken: source.token,
    })
    instance.interceptors.request.use((config) => {
      if (token) {
        config.headers = {
          ...config.headers,
          Authorization: `Bearer ${token}`,
        }
      }
      return config
    })
    instance
      .get(url)
      .then((response) => {
        isFulfilled = true
        stopLoading?.()

        resolve({ status: response.status, content: response })
      })
      .catch((error) => {
        isFulfilled = true
        stopLoading?.()

        handleServiceError(error, handleErrorInternally, reject)
      })

    setTimeout(() => {
      if (!isFulfilled) {
        source.cancel('NETWORK_ERROR')
      }
    }, errorTimeout)
  })
}

export const post = async (
  url: string,
  data: any,
  handleErrorInternally: boolean = true,
  contentType: string = 'application/json',
  avoidServiceCallsNotification: boolean = false,
  forceHandleAllErrors: boolean = false,
): Promise<any> => {
  const token = await getValidAccessToken()
  return new Promise((resolve, reject) => {
    const CancelToken = axios.CancelToken
    const source = CancelToken.source()

    let isFulfilled = false
    const stopLoading = avoidServiceCallsNotification ? null : startLoading()

    const instance = axios.create({
      baseURL: serviceURL,
      timeout: timeout,
      cancelToken: source.token,
      headers: {
        'Content-type': contentType,
      },
    })
    instance.interceptors.request.use((config) => {
      if (token) {
        config.headers = {
          ...config.headers,
          Authorization: `Bearer ${token}`,
        }
      }
      return config
    })

    instance
      .post(url, data)
      .then((response) => {
        isFulfilled = true
        stopLoading?.()

        resolve({ status: response.status, content: response })
      })
      .catch((error) => {
        isFulfilled = true
        stopLoading?.()

        handleServiceError(
          error,
          handleErrorInternally,
          reject,
          null,
          null,
          forceHandleAllErrors,
        )
      })

    setTimeout(() => {
      if (!isFulfilled) {
        source.cancel('NETWORK_ERROR')
      }
    }, errorTimeout)
  })
}

const determineErrorPageUrl = () => {
  const index = window.location.pathname.indexOf('/company/')
  if (index !== -1) {
    let companyId = window.location.pathname.substring(index + 9)
    companyId = companyId.substring(0, companyId.indexOf('/'))
    return ROUTES.GenericErrorWithCompany.replace(':companyId', companyId)
  }
  return ROUTES.GenericError
}

const handleServiceError = (
  error: any,
  handleErrorInternally: boolean,
  reject: (reason?: any) => void,
  msg: string = null,
  code: string = null,
  forceHandleAllErrors: boolean = false,
) => {
  const message = msg != null ? msg : prepareErrorMessage(error)

  // Uwaga: obie te sciezki i wczesniej nie pokazywaly uzytkownikowi niczego -
  // `serviceCallError` zapisywalo pola, ktorych nikt nie odczytywal. Zachowanie
  // zostaje bez zmian; podniesienie tego do komunikatu to osobna decyzja.
  if (error.code === 'ECONNABORTED' || error.response == null) {
    return
  }

  if (
    !forceHandleAllErrors &&
    (handleErrorInternally ||
      STATUS_CODES_FOR_INTERNAL_HANDLING.find(
        (s) => s == error.response.status,
      ))
  ) {
    if (window.location.pathname.includes('/error')) {
      return
    }

    if (error.response.data && error.response.data.ErrorCode === 'NO_CLAIM') {
      void handleUnauthorized()
      return
    }

    if (dispatchIpNotAllowedModal(error)) return

    // Obsłużone modalem przez GlobalErrorHandler — nie wyrzucaj na stronę błędu.
    // Ta warstwa nie przechodzi przez interceptor `@/lib/axios`, więc zdarzenie
    // trzeba wysłać samemu; bez tego modal by się nie pokazał.
    //
    // NOT_MIGRATED_COMPANY szło wcześniej na stronę błędu. Modal mówi to samo, ale
    // nazywa firmę, sam się odświeża po migracji i pozwala przełączyć firmę - a
    // nawigacja i tak by go zdjęła (GlobalErrorHandler czyści stan przy zmianie ścieżki).
    const companyErrorCode = error.response.data?.ErrorCode
    if (
      companyErrorCode === 'COMPANY_HAS_NO_DATABASE' ||
      companyErrorCode === 'NOT_MIGRATED_COMPANY'
    ) {
      dispatchCompanyErrorModal(companyErrorCode, error)
      return
    }

    switch (error.response.status) {
      case 401:
        void handleUnauthorized()
        break
      case 404:
        const url = determineErrorPageUrl()
        showGenericError({
          description: message,
          statusCode: error.response.status,
        })
        navigator.go(url)
        break
      case 500:
      case 504:
        // Bez zamiennika - patrz komentarz przy ECONNABORTED.
        break
      case 403:
        showMessage({
          text: 'errorCodes.forbidden',
          details: message,
          type: MessageType.ERROR,
          translate: true,
        })
        break
      case 400:
        if (typeof code === 'string' && code.startsWith('EMPTY_DOCUMENT')) {
          showMessage({
            text: `errorCode.${code}`,
            type: MessageType.WARNING,
            translate: true,
          })
        }
        // Gałąź `else` była martwa (serviceCallError) - zachowujemy brak komunikatu.
        break
      default:
        showMessage({ text: message, type: MessageType.ERROR })
    }
  } else {
    reject({
      status: axios.isCancel(error)
        ? 'exception.service-unavailable'
        : error.response,
    })
  }
}

const prepareErrorMessage = (error: any) => {
  let message = error.message

  if (error.code === 'ECONNABORTED') {
    message = 'exception.timeout'
  } else if (error.response == null) {
    message = 'exception.no-internet-connection'
  } else {
    switch (error.response.status) {
      case 401:
        message = 'exception.not-authorized'
        break
      case 403:
        message = 'exception.forbidden'
        break
      case 404:
        message = 'exception.not-found'
        break
      case 500:
        message = 'exception.unexpected-exception'
        break
      case 504:
        message = 'exception.service-unavailable'
        break
    }
  }
  return message
}

// 401/NO_CLAIM z backendu: spróbuj cicho odświeżyć token i przeładuj stronę
// (stare ekrany nie umieją ponowić pojedynczego requestu); pełny redirect na
// Keycloak dopiero, gdy refresh token też jest martwy.
const handleUnauthorized = async () => {
  try {
    const refreshedUser = await userManager.signinSilent()
    if (refreshedUser?.access_token) {
      window.location.reload()
      return
    }
  } catch {
    /* silent refresh failed */
  }
  redirectToLogin()
}

const openFile = (url, name) => {
  const link = document.createElement('a')
  link.download = name
  link.href = url
  link.setAttribute('target', '_blank')
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

const resolveDownloadFileName = (response: any): string => {
  const header =
    response.headers['content-disposition'] ||
    response.headers['Content-Disposition']
  if (header) {
    try {
      const parsed = contentDisposition.parse(header)
      const fn = parsed.parameters.filename
      if (fn != null && String(fn).length > 0) {
        return String(fn)
      }
    } catch {
      /* fallback below */
    }
  }
  return extractFileNameLegacy(response)
}

const extractFileNameLegacy = (response: any): string => {
  const header =
    response.headers['content-disposition'] ||
    response.headers['Content-Disposition']
  if (!header) {
    return 'attachment.pdf'
  }
  const parts = header.split('filename=')
  if (parts.length < 2) {
    return 'attachment.pdf'
  }
  const filename = parts[1]
    .split(';')[0]
    .replace(/^["']|["']$/g, '')
    .trim()
  if (!filename) {
    return 'attachment.pdf'
  }
  if (/^[A-Za-z0-9+/=]+$/.test(filename)) {
    try {
      return base64DecodeUnicode(filename)
    } catch {
      return filename
    }
  }
  return filename
}

const base64DecodeUnicode = (str) => {
  return decodeURIComponent(
    atob(str)
      .split('')
      .map(function (c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
      })
      .join(''),
  )
}
